import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { Info, ShieldCheck, Trash2 } from 'lucide-react';

const StaffManagementPage = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [errors, setErrors] = useState({});

  const load = () => {
    setLoading(true);
    api
      .get('/staff')
      .then((res) => setStaff(res.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = await api.post('/staff', form);
      toast({
        title: 'Staff user added',
        description: `Temporary password: ${res.tempPassword} (share this with them manually — email invites aren't wired up yet)`,
      });
      setForm({ name: '', phone: '', email: '' });
      setOpen(false);
      load();
    } catch (error) {
      if (error?.errors) {
        setErrors(error.errors);
      }
      toast({
        title: 'Failed to add staff user',
        description: error?.error?.message || error?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff user? They will no longer be able to log in.')) return;
    try {
      await api.delete(`/staff/${id}`);
      setStaff((prev) => prev.filter((s) => s.id !== id));
      toast({ title: 'Staff user removed' });
    } catch (error) {
      toast({ title: 'Failed to remove staff user', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Staff Management</h1>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-11 h-11 rounded-lg border-2 border-gray-300 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Staff User</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Staff users are mainly event management team present at the event venue, to
              verify/validate invitee access by scanning the QR Invitation cards at entrance of
              the event and sub events/sessions/breaks etc. They get their own login to use the
              QR scanner.
            </p>
            <Button className="bg-green-600 hover:bg-green-700 mt-4" onClick={() => setOpen(true)}>
              + Add New
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : staff.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No staff users added yet.</p>
        ) : (
          <div className="divide-y">
            {staff.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
                    {s.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      {s.name}
                      <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                    </p>
                    <p className="text-xs text-gray-500">
                      {s.email}
                      {s.phone ? ` · ${s.phone}` : ''}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="staffName">User Name *</Label>
              <Input
                id="staffName"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <p className="text-xs text-gray-400 mt-1">
                Character limit is 100. Only alphabets allowed (a-z, A-Z), no special characters
                allowed !, @, #, $, %, &amp;, * etc.
              </p>
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name[0]}</p>}
            </div>
            <div>
              <Label htmlFor="staffPhone">Contact No.</Label>
              <Input
                id="staffPhone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="staffEmail">Email *</Label>
              <Input
                id="staffEmail"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email[0]}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleAdd}
              disabled={saving || !form.name || !form.email}
            >
              {saving ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementPage;
